import { apiService } from './api';

// ── Printer profiles ─────────────────────────────────────────────────────────

/** Mirrors backend PrinterType enum. */
export enum PrinterType {
  A4 = 0,
  Thermal = 1,
}

export interface PrinterProfile {
  id: number;
  name: string;
  printerName: string;
  printerType: PrinterType;
  paperWidthMm: number;
  isDefault: boolean;
  kickCashDrawer: boolean;
  isActive: boolean;
}

export interface CreatePrinterProfileRequest {
  name: string;
  printerName: string;
  printerType: PrinterType;
  paperWidthMm: number;
  isDefault: boolean;
  kickCashDrawer: boolean;
}

export interface UpdatePrinterProfileRequest extends CreatePrinterProfileRequest {
  isActive: boolean;
}

// ── Print agents ─────────────────────────────────────────────────────────────

export interface PrintAgent {
  id: number;
  name: string;
  isActive: boolean;
  lastSeenAt: string | null;
  createdAt: string;
}

/** The apiKey is returned exactly once — it cannot be retrieved later. */
export interface RegisterPrintAgentResult {
  id: number;
  name: string;
  apiKey: string;
}

// ── Print jobs ───────────────────────────────────────────────────────────────

/** Mirrors backend PrintJobStatus enum. */
export enum PrintJobStatus {
  Queued = 0,
  Dispatched = 1,
  Completed = 2,
  Failed = 3,
}

export interface PrintJob {
  id: number;
  documentType: string;
  documentReference: string;
  printerName: string;
  payloadType: number; // 0 = Pdf, 1 = EscPos
  status: PrintJobStatus;
  attempts: number;
  errorMessage: string | null;
  createdAt: string;
  completedAt: string | null;
}

const PROFILES = '/printer-profiles';
const AGENTS = '/print-agents';
const JOBS = '/print-jobs';

export const printerService = {
  // Profiles
  getProfiles: () => apiService.get<PrinterProfile[]>(PROFILES),
  /** Active profiles only; needs sales permission, not settings — for the sales screen. */
  getProfilesForPrinting: () =>
    apiService.get<PrinterProfile[]>(`${PROFILES}/for-printing`),
  createProfile: (data: CreatePrinterProfileRequest) =>
    apiService.post<PrinterProfile>(PROFILES, data),
  updateProfile: (id: number, data: UpdatePrinterProfileRequest) =>
    apiService.put<PrinterProfile>(`${PROFILES}/${id}`, data),
  deleteProfile: (id: number) => apiService.delete(`${PROFILES}/${id}`),

  // Agents
  getAgents: () => apiService.get<PrintAgent[]>(AGENTS),
  registerAgent: (name: string) =>
    apiService.post<RegisterPrintAgentResult>(AGENTS, { name }),
  revokeAgent: (id: number) => apiService.delete(`${AGENTS}/${id}`),

  // Jobs
  getJobs: () => apiService.get<PrintJob[]>(JOBS),
  retryJob: (id: number) => apiService.post<PrintJob>(`${JOBS}/${id}/retry`, {}),
};
